/**
 * Undo and redo for the proposal canvas.
 *
 * A stack of whole drafts, not of operations: a draft is small, and a snapshot
 * cannot drift from the thing it restores. Typing into one field coalesces —
 * every change with the same `group` inside a second is one step — so undo
 * takes back a sentence and not a letter. Undo and redo go through the
 * builder's own `update`, so they autosave like any other edit.
 */
import { useCallback, useEffect, useReducer, useRef } from 'react'
import type { ProposalDraft } from './draft.js'

export type DraftUpdate = (change: (draft: ProposalDraft) => ProposalDraft) => void
export type DraftApply = (change: (draft: ProposalDraft) => ProposalDraft, group?: string) => void

const COALESCE_MS = 1000
const DEPTH = 100

export function useDraftHistory(draft: ProposalDraft, update: DraftUpdate) {
  const past = useRef<ProposalDraft[]>([])
  const future = useRef<ProposalDraft[]>([])
  const last = useRef<{ group: string | null; at: number }>({ group: null, at: 0 })
  const current = useRef(draft)
  const [, rerender] = useReducer((count: number) => count + 1, 0)

  useEffect(() => {
    current.current = draft
  }, [draft])

  const apply = useCallback<DraftApply>(
    (change, group) => {
      const now = Date.now()
      const coalesce = group !== undefined && last.current.group === group && now - last.current.at < COALESCE_MS
      if (!coalesce) {
        past.current.push(current.current)
        if (past.current.length > DEPTH) past.current.shift()
      }
      future.current = []
      last.current = { group: group ?? null, at: now }
      update((d) => {
        const next = change(d)
        current.current = next
        return next
      })
      rerender()
    },
    [update],
  )

  const step = useCallback(
    (from: typeof past, to: typeof past) => {
      const target = from.current.pop()
      if (!target) return
      to.current.push(current.current)
      last.current = { group: null, at: 0 }
      current.current = target
      update(() => target)
      rerender()
    },
    [update],
  )

  const undo = useCallback(() => step(past, future), [step])
  const redo = useCallback(() => step(future, past), [step])

  return {
    apply,
    undo,
    redo,
    canUndo: past.current.length > 0,
    canRedo: future.current.length > 0,
  }
}
